import { capitalize } from 'lodash';
import { DATE_FORMATS, FILE_MIME_TYPES, PREPOSITIONS } from '@constants/generic-constants.js';
import { convertQueryObjToString, getQueryString, mapQueryStringToFilterObject } from '@components-web/filters/helper';
import { OMS_PATH, BASE_PATH } from '@constants/seller-center-paths.js';
import dayjs from 'dayjs';
import { Toast } from '@common-web';
import * as yup from 'yup';
import { strings } from '@constants/strings';
import { dubizzleLogoutApi } from '@apis/seller-center/profile-settings';
import { store } from '../store';
import { setAuthorized, setUserInfo } from '@reducers/seller-center/userinfo-slice';
import { getFileUrlApi } from '@apis/api-helper';
import { getTenantFromURL, isDevMode } from './app-utils';
import { removeCookie } from '../auth/helper/helper';
import ServerError from '../pages/seller-center/server-error/index.jsx';
import NotFound from '../pages/seller-center/not-found/index.jsx';
import tenantConfig from '../data/customizer/tenant-config';
import { clientQuery } from '../index';

///////////////////// Utils related to `Select` //////////////////////////////

export const formatValueForSelect = ({ obj = null, key = 'name' }) => {
  return obj ? { ...obj, label: obj?.[key] } : null;
};

export const formatOptionsForSelect = ({ list = [], setAsSlug = false, capitalizeLabel = false }) => {
  return list?.map((item) => {
    const label = item?.display_name ?? item?.name ?? item?.title ?? item?.value;
    return {
      ...item,
      value: item?.id,
      label: capitalizeLabel ? capitalize(label) : label,
      name: item?.display_name ?? item?.name ?? item?.title ?? item?.value,
      slug: setAsSlug ? item?.slug : item?.id,
    };
  });
};

export const fetchInitialValuesForInfiniteSelect = async ({ location, filtersList, setter, api, filterKey }) => {
  const filtersObj = mapQueryStringToFilterObject(location.search)?.queryObj;
  let newItems = [];
  for (const filter of filtersList) {
    const itemFilterObj = {};
    if (filter?.key === filterKey) {
      itemFilterObj[filter?.queryKey] = filtersObj[filter?.key];
      const query = convertQueryObjToString(itemFilterObj);
      try {
        if (query?.length) {
          const response = await api(`?${query}`);
          if (response.success) {
            newItems = [...newItems, ...response[filter?.fetchKey]];
          }
        }
      } catch (err) {
        Toast.error(getErrorString(err));
      }
    }
    return {};
  }
  setter(newItems);
};

/////////////////////////////////////////////////////////////////////////////

///////////////////// Utils related to `Date` //////////////////////////////

export const getFormattedDate = (date, format = DATE_FORMATS.monthDayYearTime) => {
  return date ? dayjs(date).format(format) : '-';
};

export const getFormattedDateDifference = (date1, date2) => {
  if (!date1 || !date2) return '0s';
  const days = dayjs(date1)?.diff(dayjs(date2), 'days');
  const hours = dayjs(date1)?.diff(dayjs(date2), 'hours') % 24;
  const minutes = dayjs(date1)?.diff(dayjs(date2), 'minutes') % 60;
  const seconds = dayjs(date1)?.diff(dayjs(date2), 'seconds') % 60;
  return (
    `${days ? `${days}d` : ''}${hours ? ` ${hours}h` : ''}${minutes ? ` ${minutes}m` : ''}${!days && !hours && !minutes && seconds ? ` ${seconds}s` : ''}` ||
    '0s'
  );
};

/////////////////////////////////////////////////////////////////////////////

///////////////////// Utils related to Numbers //////////////////////////////

export const formatNumbers = (number, round = true, compact = false) => {
  if (!number) {return 0};
  
  const cleanedNumber = number.toString().replace(/,/g, '');
  let parsedNumber = parseFloat(cleanedNumber);
  
  if (round) {
    parsedNumber = Math.round(parsedNumber);
  }
  
  if (compact && Math.abs(parsedNumber) >= 1000000) {
    return new Intl.NumberFormat('en', { 
      notation: 'compact', 
      maximumFractionDigits: 2 
    }).format(parsedNumber);
  }
  
  return parsedNumber.toLocaleString('en-US', { 
    maximumFractionDigits: 2,
  });
};

export const numberToWords = (number) => {
  if (!Number.isInteger(number)) {
    throw new Error('Input must be an integer');
  }
  if (number < 0 || number > 999_999_999_999) {
    throw new Error('Number must be between 0 and 999,999,999,999');
  }
  if (number === 0) return 'Zero';

  const ones = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
  ];

  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const scales = ['', 'Thousand', 'Million', 'Billion'];

  const convertHundreds = (n) => {
    const parts = [];
    if (n >= 100) {
      parts.push(ones[Math.floor(n / 100)], 'Hundred');
      n %= 100;
      if (n > 0) parts.push('and');
    }
    if (n >= 20) {
      parts.push(tens[Math.floor(n / 10)]);
      if (n % 10) parts.push(ones[n % 10]);
    } else if (n > 0) {
      parts.push(ones[n]);
    }
    return parts.join(' ');
  };

  const groups = [];
  while (number > 0) {
    groups.push(number % 1000);
    number = Math.floor(number / 1000);
  }

  return groups
    .map((group, i) => (group ? `${convertHundreds(group)} ${scales[i]}`.trim() : ''))
    .filter(Boolean)
    .reverse()
    .join(' ');
};

export const getAggregationsCount = (label, aggregations = {}) => {
  return aggregations?.[label]?.toLocaleString() || 0;
};

export const formatPhoneNo = (phoneNumber, tenant) => {
  if (!phoneNumber) return;

  if (tenant === 'OLX-PK') {
    return phoneNumber.replace(/(\+92)(\d{3})(\d+)/, '$1 $2 $3');
  }
  if (tenant === 'DUBIZZLE-UAE') {
    return phoneNumber.replace(/(\+971)(\d{1,2})(\d{6,7})/, '$1 $2 $3');
  } else {
    return phoneNumber;
  }
};

/////////////////////////////////////////////////////////////////////////////

///////////////////// Utils related to `Formik` //////////////////////////////

export const setFormikValues = (updatedValues, formikRef) => {
  const values = {
    ...(updatedValues ? formikRef?.current && formikRef?.current?.values : {}),
    ...updatedValues,
  };
  formikRef.current && formikRef.current.setValues(values);
};

export const isValidPhoneNumber = ({ phoneString = '' }) =>
  phoneString &&
  (isOLXTenant()
    ? /^\+923[0-9]{9}$/.test(phoneString)
    : /^\+971[0-9]{8}$/.test(phoneString) || /^\+971[0-9]{9}$/.test(phoneString));

export const validatePhoneNumber = (tenant = 'OLX-PK') => {
  return yup.string().test('isValidPhoneNumber', strings.field_not_valid_msg, function (value) {
    return !value?.length || isValidPhoneNumber({ phoneString: value, tenant });
  });
};

export const getQuillLengthValidation = ({ value = '', min = 0, max = 0 }) => {
  if (value?.length) {
    const textContent = value?.replace(/<[^>]+>/g, '')?.trim();
    if (textContent?.length < min || textContent?.length > max) {
      return false;
    }
  }
  return true;
};

/////////////////////////////////////////////////////////////////////////////

///////////////////// Miscellaneous /////////////////////////////////////////

export const getErrorString = (error) => {
  if (error?.requestError?.code === 'ERR_CANCELED') return '';
  const errors = error?.requestError?.response?.data?.errors || error?.error?.response?.data?.errors;
  if (typeof errors === 'string') return errors;
  if (errors?.length) {
    return errors.join(', ');
  }
};

export const generateUUID = () => {
  var dt = new Date().getTime();
  var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    var r = ((dt + Math.random() * 16) % 16) | 0;
    dt = Math.floor(dt / 16);
    return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
  return uuid;
};

export const slugToPlainText = (str) => {
  return makeTitleCaseExcludingPrepositions({ string: str?.replace(/[_-]/g, ' ') });
};

export const isEmptyObject = (obj) =>
  obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;

export const isTypeObject = (obj) => typeof obj === 'object' && !Array.isArray(obj) && obj !== null;

export function clone(obj) {
  if (typeof obj == 'function') {
    return obj;
  }
  let result = Array.isArray(obj) ? [] : {};
  for (let key in obj) {
    // include prototype properties
    let value = obj[key];
    if (value && (Object.getPrototypeOf(value) === Object.prototype || Array.isArray(value))) {
      result[key] = clone(value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

export function formatImagesForPrefilling({ images = [] }) {
  return images?.map((item) => ({
    uid: item?.id,
    name: generateUUID(),
    type: 'image/jpeg',
    percent: 0,
    originFileObj: {
      uid: item?.id,
    },
    id: item?.id,
    hasUuid: true,
    hasPreSignedUrl: true,
    preview: item?.sizes?.medium ?? item?.sizes?.large ?? item?.sizes?.small ?? item?.sizes?.document,
    previewSmall: item?.sizes?.small ?? item?.sizes?.document,
    previewLarge: item?.sizes?.large ?? item?.sizes?.document,
  }));
}

export function formatFilesForPrefilling({ files = [] }) {
  return files?.map((item) => ({
    uid: item?.id,
    name: item?.name ?? generateUUID(),
    type:
      FILE_MIME_TYPES?.[
        item?.name ? item?.name?.match(/\.[0-9a-z]+$/i)?.[0] : item?.sizes?.document?.match(/\.[0-9a-z]+$/i)?.[0]
      ] ?? '',
    percent: 0,
    originFileObj: {
      uid: item?.id,
    },
    id: item?.id,
    hasUuid: true,
    hasPreSignedUrl: true,
    preview: item?.sizes?.document,
  }));
}

export const makeTitleCase = ({ string = '', seperator = ' ' }) => {
  return string
    .split(seperator)
    .map((word) => capitalize(word))
    .join(seperator);
};

export const makeTitleCaseExcludingPrepositions = ({ string = '', seperator = ' ' }) => {
  return string
    .split(seperator)
    .map((word, index) =>
      index === 0 || !PREPOSITIONS?.includes(word.toLowerCase()) ? capitalize(word) : word.toLowerCase()
    )
    .join(seperator);
};

export const formatCurrency = ({ price = '', currency = '', negative = false, round = true, compact = false }) => {
  return `${negative && parseFloat(price) > 0 ? '- ' : ''}${currency} ${formatNumbers(price, round, compact)}`;
};

export const isNumericString = (string) => {
  if (typeof string === 'number' || string === '') return true;
  const regex = /[^0-9.]/;
  return !regex.test(string);
};

export const convertNewlinesToBr = (text = '') => {
  return text?.split('\n')?.map((item, key) => ({
    key,
    content: item,
  }));
};

export const clearTextSelectionDBClick = () => {
  if (document.selection && document.selection.empty) {
    document.selection.empty();
  } else if (window.getSelection) {
    let sel = window.getSelection();
    sel.removeAllRanges();
  }
};

export const formatFloatingNumbers = (number, decimalPlaces = 1) => {
  return number ? parseFloat(number)?.toFixed(decimalPlaces) : 0;
};

export const getMapBoxAccessToken = () => process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? '';

export const isOMS = (options = { location: window.location }) => {
  const { location } = options;
  return location.pathname.includes(OMS_PATH);
};

export const changeTabFromSearchQuery = ({ location = {}, statuses = [], setTabIndex = () => {}, dependentFn = () => {} }) => {
  const { queryObj } = mapQueryStringToFilterObject(location?.search);
  if (queryObj['f[status.slug]'] || queryObj['f[disposition.slug]']) {
    const slug = queryObj['f[disposition.slug]'] || queryObj['f[status.slug]'];
    const index = statuses?.findIndex((item) => item?.slug === slug);
    setTabIndex(index + 1);
    dependentFn(index);
  } else {
    setTabIndex(0);
    dependentFn(-1);
  }
};

export const onTabClickChange = ({ key, statuses = [], nav, dependentFn = () => {} }) => {
  const value = statuses[key - 1];
  let { queryObj } = mapQueryStringToFilterObject(location.search);
  if ('page' in queryObj) queryObj['page'] = 1;
  queryObj[value?.type === 'disposition' ? 'f[disposition.slug]' : 'f[status.slug]'] = value?.slug;
  delete queryObj?.[value?.type === 'disposition' ? 'f[status.slug]' : 'f[disposition.slug]'];
  delete queryObj?.['f[disposition.slug][]'];
  dependentFn(key - 1, queryObj);
  const query = getQueryString(queryObj);
  nav({
    search: query,
  });
};

export const getAllImages = (images) => {
  let allImages = [];
  if (images.length) {
    images.forEach((image) => {
      allImages.push({
        url: image?.sizes?.thumbnail,
        preview: image?.sizes?.large,
        rejected: false,
        id: image?.id,
      });
    });
  }
  return allImages;
};

export const removeWhiteSpaces = (string) => string.replace(/\s+/g, '');

export const joinWithSpaces = (array) => array?.join(' ')?.trim() ?? '';

export const isEmptyString = (string) => string === '';

export const isScrollEnd = (e) =>
  Math.abs(Math.round(e?.target?.clientHeight + e?.target?.scrollTop) - Math.round(e?.target?.scrollHeight)) <= 1;

export const sanitizePathname = (path = '') => path.replace(/\/+$/, '');

export const ifValueExists = (value) => value !== null && value !== undefined;

export const isBoolean = (value) => value === true || value === false;

export const handleDocumentPrint = (documentUrl) => {
  if (documentUrl) {
    window.open(documentUrl, '_blank');
  }
};

export const handleDocumentDownload = async (documentUrl, fileName) => {
  if (documentUrl) {
    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const link = document.createElement('a');
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', fileName);
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      Toast.error(getErrorString(err));
    }
  }
};

export const isOLXTenant = () => getTenantFromURL() === 'OLX-PK';
export const isDubizzleTenant = () => getTenantFromURL() === 'DUBIZZLE-UAE';

export const handleOMSLogout = async () => {
  try {
    // if (isOLXTenant()) {
    //   const kc = await keycloak;
    //   const refreshToken = kc.refreshToken;
    //   await Promise.all([olxLogoutApi(new URLSearchParams({ client_id: 'frontend', refresh_token: refreshToken })), kc.logout()]);
    //   window.location.href = import.meta.env.VITE_APP_CONSUMER_URL;
    // } else {
    await dubizzleLogoutApi();
    removeCookie('local_sc_access_token');
    clientQuery?.clear();
    store.dispatch(setAuthorized(false));
    store.dispatch(setUserInfo(null));
    // }
  } catch (err) {
    Toast.error(getErrorString(err));
  }
};

export const redirectToPath = (path) => {
  const fullPath = `${BASE_PATH}${path}`;
  if (sanitizePathname(window.location.pathname) !== fullPath) {
    window.location.href = fullPath;
  }
  return;
};

export const formatQuillOutput = async (description, productName) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(description, 'text/html');

  const imgTags = doc.querySelectorAll('img');
  const description_image_ids = [];

  const apiCalls = [];

  imgTags.forEach((img, index) => {
    const src = img.getAttribute('src');
    // Add fetchpriority="low" to all image tags
    img.setAttribute('fetchpriority', 'low');
    if (tenantConfig?.product?.add_product?.image_lazy_loading) {
      img.setAttribute('loading', 'lazy');
    }

    if (src) {
      // handles case where image is uploaded by user itself
      if (src?.includes('#')) {
        const id = src.split('#')[1];
        const apiCall = getFileUrlApi({ id })
          .then((data) => {
            const newSrc = data?.attachment?.sizes?.large;
            img.setAttribute('src', newSrc);
            img.setAttribute('alt', `${productName} - ${index+1}`);
            img.setAttribute('data-image-id', id);
            description_image_ids?.push(id);
            return data;
          })
          .catch((error) => {
            Toast.error(getErrorString(error));
          });

        apiCalls.push(apiCall);
      } else {
        // if src contains dubizzle or olx, only then push it to description ids
        if (src?.includes(`/${getTenantFromURL()?.toLowerCase()}`)) {
          const pathSplit = src?.split('/');
          if (pathSplit?.length > 2) {
            const id = pathSplit?.[pathSplit?.length - 2];
            description_image_ids?.push(id);
            img.setAttribute('alt', `${productName} - ${index+1}`);
            img.setAttribute('data-image-id', id);          }
        }
      }
    }
  });

  try {
    await Promise.all(apiCalls);
  } catch (err) {
    Toast.error(getErrorString(err));
  }

  return { description: doc.body?.innerHTML, description_image_ids };
};

export function generateEmbedUrl(url) {
  if (url.includes('youtube.com/watch?v=')) {
    const videoId = url?.split('v=')?.[1]?.split('&')?.[0];
    return `https://www.youtube.com/embed/${videoId}`;
  } else if (url.includes('youtu.be/')) {
    const videoId = url?.split('youtu.be/')?.[1]?.split('?')?.[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
}

export const slugify = (str = '') => str?.split(' ')?.join('-')?.toLowerCase();

export const getErrorPage = (status) => {
  switch (status) {
    case 500:
      return ServerError;
    case 404:
      return NotFound;
    case 422:
      return NotFound;
    default:
      return null;
  }
};

export const formatSeconds = (totalSeconds) => {
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  const isLongerThanHour = totalSeconds >= 3600;

  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (!isLongerThanHour && (seconds > 0 || parts.length === 0)) parts.push(`${seconds}s`);

  return parts.join(' ');
};

export const getQueryParams = ({location, pageSize = 20}) => {
  const queryObject = mapQueryStringToFilterObject(location?.search || '');
  const searchQuery = getQueryString({ ...queryObject?.queryObj, limit: pageSize });
  return searchQuery === '?' ? '' : searchQuery;
}

export const createConditionalObject = (obj, condition, properties) => {
  if (condition) {
    Object.assign(obj, properties);
  }
};

export const capitalizeFirstLetter = (str) => {
  if (typeof str !== 'string' || str.length === 0) {
    return "";
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const isTodayDateRange = (startDate, endDate) => {
  if (!startDate && !endDate) return true;

  if (!startDate || !endDate) return false;

  const today = dayjs().startOf('day');

  return (
    dayjs(startDate).startOf('day').isSame(today) &&
    dayjs(endDate).startOf('day').isSame(today)
  );
};


/////////////////////////////////////////////////////////////////////////////
